import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ForumSeo } from '@/components/ForumSeo'
import {
  IconArrowLeft,
  IconBookmark,
  IconBookmarkFilled,
  IconChevronDown,
  IconEyeOff,
  IconFolder,
  IconLoader2,
  IconLock,
  IconPin,
  IconQuote,
  IconShieldLock,
  IconTrash,
} from '@tabler/icons-react'
import { RichContent } from '@/components/richtext/RichContent'
import { RichEditor } from '@/components/richtext/RichEditor'
import { StaffRoleIcon } from '@/components/forum/StaffRoleIcon'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'
import { apiFetch } from '@/lib/api'
import { useAuthLoading, useConfirmedGuest, useSessionUser } from '@/lib/auth'
import type { ForumCategorySummary } from '@/lib/types'
import { clientSiteOrigin } from '@/lib/siteOrigin'
import {
  forumAlertWarn,
  forumCard,
  forumCardHeader,
  forumEmptyBox,
  forumKicker,
  forumLink,
  forumLoaderRow,
  forumMutedSm,
  forumSectionBar,
  forumTitleLg,
} from '@/lib/forumUi'
import { forumPageTitle, truncateForMetaDescription } from '~shared/seo'

type Viewer = {
  isModerator: boolean
  isOriginalPoster: boolean
}

export function ThreadPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  type PostRow = {
    id: string
    threadId: string
    quotedPostId?: string | null
    content: unknown
    deleted: boolean
    editedAt?: number | null
    createdAt: number
    reactions: Record<string, number>
    author: {
      id: string
      username: string
      displayName: string
      avatarUrl: string | null
      role: string
      createdAt: number
    }
  }

  type ThreadPayload = {
    id: string
    title: string
    slug: string
    category: { slug: string; name: string }
    bookmarked?: boolean
    pinned: boolean
    locked: boolean
    hidden: boolean
    viewCount: number
    replyCount: number
    excerpt: string
    posts: PostRow[]
    viewer: Viewer
  }

  const [thread, setThread] = useState<ThreadPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [composer, setComposer] = useState<unknown>(() => ({
    type: 'doc',
    content: [{ type: 'paragraph' }],
  }))
  const [quotedId, setQuotedId] = useState<string | null>(null)
  const [modToolsOpen, setModToolsOpen] = useState(false)

  useEffect(() => {
    setModToolsOpen(false)
  }, [slug])

  useEffect(() => {
    let alive = true
    if (!slug) return

    setLoading(true)

    void apiFetch<{ thread: ThreadPayload }>(`/api/threads/${encodeURIComponent(slug)}`)
      .then(payload => {
        if (!alive) return
        setThread(payload.thread)
        setError(null)
      })
      .catch(() => {
        if (!alive) return
        setError('Could not load this thread.')
      })
      .finally(() => {
        if (!alive) return
        setLoading(false)
      })

    return () => {
      alive = false
    }
  }, [slug])

  const me = useSessionUser()
  const authLoading = useAuthLoading()
  const guest = useConfirmedGuest()

  const modPanel = thread?.viewer.isModerator

  const moderationInitial = useMemo(
    () => ({
      pinned: !!thread?.pinned,
      locked: !!thread?.locked,
      hidden: !!thread?.hidden,
      categorySlug: thread?.category.slug ?? '',
      categoryName: thread?.category.name ?? '',
    }),
    [
      thread?.category.name,
      thread?.category.slug,
      thread?.hidden,
      thread?.locked,
      thread?.pinned,
    ],
  )

  const threadLd = useMemo(() => {
    if (!thread) return undefined
    const base = clientSiteOrigin()
    const url = `${base}/t/${thread.slug}`
    const ld: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'DiscussionForumPosting',
      headline: thread.title,
      url,
    }
    if (thread.category?.name) ld.articleSection = thread.category.name
    if (thread.excerpt?.trim()) {
      ld.articleBody = truncateForMetaDescription(thread.excerpt.trim(), 400)
    }
    return ld
  }, [thread])

  const refresh = async () => {
    if (!slug) return
    const payload = await apiFetch<{ thread: ThreadPayload }>(`/api/threads/${encodeURIComponent(slug)}`)
    setThread(payload.thread)
  }

  const toggleBookmark = async (t: ThreadPayload) => {
    if (!slug) return
    const body = { threadSlug: slug }
    await (t.bookmarked
      ? apiFetch(`/api/bookmarks`, {
          method: 'DELETE',
          json: body,
        })
      : apiFetch(`/api/bookmarks`, {
          method: 'POST',
          json: body,
        }))
    await refresh()
  }

  if (loading) {
    return (
      <div role="status" aria-busy className={forumLoaderRow}>
        <IconLoader2 className="size-5 shrink-0 animate-spin" aria-hidden="true" />
        Loading topic…
      </div>
    )
  }

  if (error || !thread) {
    return (
      <section aria-labelledby="thread-missing-heading" className={`${forumCard} px-5 py-8 md:px-6 md:py-10`}>
        <h1 id="thread-missing-heading" className={forumTitleLg}>
          Thread unavailable
        </h1>
        <p className={`mt-2 max-w-[60ch] text-[15px] leading-relaxed text-zinc-600`}>{error ?? 'Nothing to show.'}</p>
        <Button type="button" className="mt-6 rounded-lg border-zinc-200 font-semibold" variant="outline" onClick={() => navigate('/')}>
          Forum index
        </Button>
      </section>
    )
  }

  const threadMetaDescription =
    thread.excerpt?.trim()?.length ?
      truncateForMetaDescription(thread.excerpt.trim())
    : truncateForMetaDescription(`${thread.title} | ${thread.category.name} on NK Our Town.`)

  return (
    <article className="space-y-6" aria-labelledby="thread-title-heading">
      <ForumSeo
        title={forumPageTitle(thread.title)}
        description={threadMetaDescription}
        ogType="article"
        jsonLd={threadLd}
      />
      <header className={forumCard}>
        <div className={`${forumCardHeader} pb-5`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className={`${forumKicker} mb-2`}>
                <Link className={`${forumLink} inline-flex items-center gap-1 text-[inherit]`} to={`/c/${thread.category.slug}`}>
                  <IconArrowLeft className="inline-block size-3.5 shrink-0" aria-hidden="true" />
                  {thread.category.name}
                </Link>
              </p>

              <h1 id="thread-title-heading" className={`${forumTitleLg} mt-0`}>
                {thread.title}
              </h1>

              <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[13px] leading-snug text-zinc-600">
                <span className="tabular-nums">{thread.viewCount.toLocaleString()} views</span>
                <span className="text-zinc-300 select-none" aria-hidden>
                  ·
                </span>
                <span className="tabular-nums">
                  {thread.replyCount.toLocaleString()} {thread.replyCount === 1 ? 'reply' : 'replies'}
                </span>
                {thread.pinned ? (
                  <>
                    <span className="text-zinc-300 select-none" aria-hidden>
                      ·
                    </span>
                    <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[12px] font-medium text-amber-900 ring-1 ring-amber-200/60 ring-inset">
                      Pinned
                    </span>
                  </>
                ) : null}
                {thread.locked ? (
                  <>
                    <span className="text-zinc-300 select-none" aria-hidden>
                      ·
                    </span>
                    <span className="rounded-md bg-zinc-100 px-2 py-0.5 text-[12px] font-medium text-zinc-700 ring-1 ring-zinc-200/80 ring-inset">
                      Locked
                    </span>
                  </>
                ) : null}
                {thread.hidden ? (
                  <>
                    <span className="text-zinc-300 select-none" aria-hidden>
                      ·
                    </span>
                    <span className="rounded-md bg-red-50 px-2 py-0.5 text-[12px] font-medium text-red-900 ring-1 ring-red-200/70 ring-inset">
                      Hidden
                    </span>
                  </>
                ) : null}
              </p>
            </div>

            <div className="flex flex-shrink-0 flex-col items-start gap-2 md:items-end">
              {authLoading ? (
                <Button variant="outline" className="gap-2 rounded-lg border-zinc-200 font-semibold" disabled aria-busy="true">
                  <IconLoader2 className="size-4 animate-spin" aria-hidden="true" />
                  <span className="sr-only">Checking session</span>
                </Button>
              ) : guest ? (
                <nav aria-label="Thread actions">
                  <Link className="no-underline" to={`/sign-in?next=/t/${thread.slug}`}>
                    <Button variant="outline" className="rounded-lg border-zinc-200 font-semibold">
                      Sign in
                    </Button>
                  </Link>
                </nav>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2 rounded-lg border-zinc-200 font-semibold"
                  onClick={() => void toggleBookmark(thread)}
                  aria-pressed={!!thread.bookmarked}
                >
                  {thread.bookmarked ? (
                    <IconBookmarkFilled className="size-4 text-sky-600" aria-hidden="true" />
                  ) : (
                    <IconBookmark className="size-4" aria-hidden="true" />
                  )}
                  {thread.bookmarked ? 'Bookmarked' : 'Bookmark'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {modPanel ? (
        <section
          className="overflow-hidden rounded-2xl border border-indigo-200/90 bg-white shadow-sm shadow-indigo-950/5"
          aria-label="Thread moderation"
        >
          <button
            type="button"
            id="mod-tools-heading"
            className={cn(
              forumSectionBar,
              'flex w-full flex-wrap items-center gap-3 border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50/80 text-left transition hover:from-indigo-100/70 hover:to-violet-100/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
            )}
            aria-expanded={modToolsOpen}
            aria-controls="mod-tools-panel"
            onClick={() => setModToolsOpen(open => !open)}
          >
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-indigo-950">
                <IconShieldLock className="size-4 shrink-0 text-indigo-700" aria-hidden="true" />
                Moderation
              </span>

              <span className="truncate font-mono text-[12px] font-medium tabular-nums text-indigo-800/90">{thread.slug}</span>
            </span>

            <IconChevronDown
              aria-hidden
              className={cn('size-5 shrink-0 text-indigo-700 transition-transform duration-200', modToolsOpen && 'rotate-180')}
            />
          </button>

          {modToolsOpen ? (
            <div
              id="mod-tools-panel"
              role="region"
              aria-labelledby="mod-tools-heading"
              className="border-t border-indigo-100/80 px-4 py-4 md:px-5 md:py-5"
            >
              <ThreadModerationBar slug={thread.slug} server={moderationInitial} onApplied={refresh} />
            </div>
          ) : null}
        </section>
      ) : null}

      <section aria-label="Forum posts">
        <h2 className="sr-only">Posts</h2>

        <ol className={`${forumCard} list-none divide-y divide-zinc-100 p-0`}>
          {thread.posts.map(post => {
            const quoteFrom = quotedPost(thread.posts, post.quotedPostId)
            return (
            <li key={post.id}>
              <article aria-labelledby={`post-${post.id}-author`}>
                <header className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-100/90 px-4 py-2.5 md:px-5 md:py-3">
                  <div>
                    <p id={`post-${post.id}-author`} className="text-[14px] font-semibold leading-snug text-zinc-950">
                      <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                        <span>{post.author.displayName}</span>
                        <StaffRoleIcon role={post.author.role} />
                        <Link className={forumLink} to={`/u/${post.author.username}`}>
                          @{post.author.username}
                        </Link>
                      </span>
                    </p>
                    <p className="mt-1 text-[12.5px] tabular-nums leading-snug text-zinc-500">
                      <time dateTime={new Date(post.createdAt * 1000).toISOString()}>{fmtRelative(post.createdAt)}</time>
                      {post.editedAt ? <span className="font-semibold italic"> · edited</span> : null}{' '}
                      {post.deleted ? <span className="font-semibold text-red-800">Removed</span> : null}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {!post.deleted && me?.id === post.author.id ? (
                      <Link className={`${forumLink} text-[13px]`} to={`/posts/${post.id}/edit`}>
                        Edit post
                      </Link>
                    ) : null}

                    {!post.deleted && (!thread.locked || thread.viewer.isModerator) ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-lg border-zinc-200 font-semibold"
                        aria-disabled={!me}
                        disabled={authLoading || guest}
                        onClick={() => {
                          setQuotedId(post.id)
                          document.getElementById('composer-heading')?.focus()
                          document.getElementById('composer-heading')?.scrollIntoView({ behavior: 'smooth' })
                        }}
                      >
                        <IconQuote className="size-4" aria-hidden="true" />
                        Quote reply
                      </Button>
                    ) : null}

                    {modPanel && !post.deleted ? (
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-2.5 py-1.5',
                          'text-[12px] font-semibold text-red-800 transition hover:bg-red-50 focus-visible:outline-none',
                          'focus-visible:ring-2 focus-visible:ring-red-500/35 focus-visible:ring-offset-2',
                        )}
                        onClick={() => {
                          if (!confirm('Soft-delete this post? The opener cannot be removed here.')) return
                          void apiFetch(`/api/moderation/posts/${post.id}/remove`, {
                            method: 'POST',
                            json: {},
                          }).then(async () => {
                            await refresh()
                          })
                        }}
                      >
                        <IconTrash className="size-3.5 shrink-0" aria-hidden />
                        Remove
                      </button>
                    ) : null}
                  </div>
                </header>

                {quoteFrom ? (
                  <aside className="border-b border-amber-200/50 bg-amber-50/45 px-4 py-2.5 text-[13px] text-amber-950 md:px-5">
                    <span className="font-medium text-amber-800/90">Quote:</span>{' '}
                    <span className="inline-flex items-center gap-1">
                      <StaffRoleIcon role={quoteFrom.author.role} /> @
                      <span className="font-semibold">{quoteFrom.author.username}</span>
                    </span>
                  </aside>
                ) : null}

                <div className="px-4 py-3.5 md:px-5 md:py-4">
                  {!post.deleted ? (
                    <RichContent doc={post.content} />
                  ) : (
                    <p className={`${forumMutedSm} text-zinc-600`}>Removed by moderator or author.</p>
                  )}
                </div>

                <footer className="flex flex-wrap items-center gap-2 border-t border-zinc-100/90 px-4 py-2 text-[13px] md:px-5">
                  {authLoading ? (
                    <p className={`${forumMutedSm} text-[13px] text-zinc-500`}>Checking your session…</p>
                  ) : me ? (
                    <>
                      <span className="sr-only" id={`reactions-label-${post.id}`}>
                        Reactions
                      </span>

                      <div className="flex flex-wrap gap-2" role="group" aria-labelledby={`reactions-label-${post.id}`}>
                        <ReactionButton
                          ariaLabel={`Like this post (${post.reactions?.like ?? 0} total)`}
                          postId={post.id}
                          type="like"
                          label={`Like (${post.reactions?.like ?? 0})`}
                          onApplied={refresh}
                        />

                        <ReactionButton
                          ariaLabel={`Mark helpful (${post.reactions?.helpful ?? 0} total)`}
                          postId={post.id}
                          type="helpful"
                          label={`Helpful (${post.reactions?.helpful ?? 0})`}
                          onApplied={refresh}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="text-[13px] font-medium text-zinc-700">
                      <Link className={forumLink} to={`/sign-in?next=/t/${thread.slug}`}>
                        Sign in
                      </Link>{' '}
                      to react.
                    </p>
                  )}
                </footer>
              </article>
            </li>
            )
          })}
        </ol>
      </section>

      {authLoading ? (
        <section className={forumEmptyBox} aria-live="polite" aria-busy="true">
          <span className="inline-flex items-center gap-2 font-medium text-zinc-600">
            <IconLoader2 className="size-4 shrink-0 animate-spin" aria-hidden="true" />
            Checking your session…
          </span>
        </section>
      ) : guest ? (
        <section className={forumEmptyBox} aria-live="polite">
          Want to reply?{' '}
          <Link className={forumLink} to={`/sign-in?next=/t/${thread.slug}`}>
            Sign in
          </Link>
          .
        </section>
      ) : thread.locked && !thread.viewer.isModerator ? (
        <section aria-label="Locked thread" role="status" className={`${forumAlertWarn} text-[14px]`}>
          Locked: replies are moderator-only until unlocked.
        </section>
      ) : (
        <section id="composer" aria-labelledby="composer-heading" className={forumCard}>
          <h2
            id="composer-heading"
            tabIndex={-1}
            className={`${forumSectionBar} rounded-t-none text-[11px] font-semibold uppercase tracking-wider`}
          >
            Post your reply
          </h2>

          <div className={`${forumMutedSm} border-t border-zinc-100 bg-sky-50/50 px-4 py-3 text-zinc-700 md:px-5`}>
            Write in the editor, then choose <kbd className="rounded border border-zinc-200 bg-white px-1.5 py-0.5 font-mono text-[11px]">Submit reply</kbd>. External links must use HTTPS.
          </div>

          {quotedId ? (
            <p className="border-t border-amber-200/80 bg-amber-50/90 px-4 py-2.5 text-[13px] font-semibold text-amber-950 md:px-5">
              Quoting @
              <span>{quotedPost(thread.posts, quotedId)?.author.username ?? '??'}</span>
              <button
                type="button"
                className="ml-3 rounded-lg border border-zinc-200 bg-white px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-800 hover:bg-zinc-50"
                onClick={() => setQuotedId(null)}
              >
                Clear quote
              </button>
            </p>
          ) : null}

          <div className="border-t border-zinc-100 p-4 md:p-5">
            <RichEditor initialJSON={composer} onChangeJSON={setComposer} />
          </div>

          <div className="flex flex-wrap gap-3 border-t border-zinc-100 px-4 py-4 md:px-5">
            <Button type="button" className="rounded-lg bg-sky-600 font-semibold text-white hover:bg-sky-500" onClick={async () => {
              await apiFetch(`/api/threads/${encodeURIComponent(thread.slug)}/replies`, {
                method: 'POST',
                json: mergedReplyPayload(composer, quotedId),
              })
              await refresh()
              setComposer({ type: 'doc', content: [{ type: 'paragraph' }] })
              setQuotedId(null)
            }}>
              Submit reply
            </Button>
          </div>
        </section>
      )}
    </article>
  )
}

function quotedPost(posts: Array<{ id: string; author: { username: string; role: string } }>, id?: string | null) {
  if (!id) return null
  return posts.find(p => p.id === id) ?? null
}

function mergedReplyPayload(content: unknown, quotedId?: string | null): Record<string, unknown> {
  return {
    content,
    ...(quotedId ? { quotedPostId: quotedId } : {}),
  }
}

function fmtRelative(ts: number): string {
  const diffMs = Date.now() - ts * 1000
  const hrs = Math.round(diffMs / 3600000)
  if (hrs < 96) return `${Math.max(1, hrs)}h ago`

  const d = new Date(ts * 1000)
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function ReactionButton(props: {
  postId: string
  type: 'like' | 'helpful'
  label: string
  ariaLabel: string
  onApplied: () => Promise<void>
}) {
  return (
    <button
      type="button"
      aria-label={props.ariaLabel}
      className={cn(
        'rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-[12px] font-semibold uppercase tracking-wide text-zinc-800',
        'transition hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40 focus-visible:ring-offset-2',
      )}
      onClick={() =>
        void apiFetch(`/api/reactions`, {
          method: 'POST',
          json: { postId: props.postId, type: props.type },
        }).then(async () => {
          await props.onApplied()
        })}
    >
      {props.label}
    </button>
  )
}

function ThreadModerationBar(props: {
  slug: string
  server: { pinned: boolean; locked: boolean; hidden: boolean; categorySlug: string; categoryName: string }
  onApplied: () => Promise<void>
}) {
  const idBoard = `mod-board-${props.slug.replace(/\W+/g, '-').replace(/^-+|-+$/g, '') || 'thread'}`
  const [pinned, setPinned] = useState(props.server.pinned)
  const [locked, setLocked] = useState(props.server.locked)
  const [hidden, setHidden] = useState(props.server.hidden)
  const [boards, setBoards] = useState<ForumCategorySummary[]>([])
  const [boardSlug, setBoardSlug] = useState(props.server.categorySlug)
  const [busyToggle, setBusyToggle] = useState<'pinned' | 'locked' | 'hidden' | null>(null)
  const [busyMove, setBusyMove] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const anyBusy = busyToggle !== null || busyMove

  useEffect(() => {
    setPinned(props.server.pinned)
    setLocked(props.server.locked)
    setHidden(props.server.hidden)
    setBoardSlug(props.server.categorySlug)
  }, [
    props.server.categorySlug,
    props.server.hidden,
    props.server.locked,
    props.server.pinned,
  ])

  useEffect(() => {
    let alive = true

    void apiFetch<{ categories: ForumCategorySummary[] }>(`/api/categories`).then(payload => {
      if (!alive) return
      setBoards(payload.categories ?? [])
    })

    return () => {
      alive = false
    }
  }, [])

  const toggleCls = (on: boolean) =>
    cn(
      'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[13px] font-semibold transition',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 focus-visible:ring-offset-2',
      'cursor-pointer disabled:cursor-not-allowed disabled:opacity-45',
      on ? 'border-indigo-600 bg-indigo-600 text-white shadow-sm hover:bg-indigo-500' : 'border-zinc-200 bg-white text-zinc-800 hover:bg-zinc-50',
    )

  async function toggle(key: 'pinned' | 'locked' | 'hidden', next: boolean) {
    if (anyBusy) return
    const prev = { pinned, locked, hidden }
    if (key === 'pinned') setPinned(next)
    if (key === 'locked') setLocked(next)
    if (key === 'hidden') setHidden(next)
    setBusyToggle(key)
    setNotice(null)

    try {
      await apiFetch(`/api/moderation/threads/${encodeURIComponent(props.slug)}`, {
        method: 'PATCH',
        json: { [key]: next },
      })

      await props.onApplied()
    } catch {
      setPinned(prev.pinned)
      setLocked(prev.locked)
      setHidden(prev.hidden)
      setNotice('Could not save; try again.')
      await props.onApplied()
    } finally {
      setBusyToggle(null)
    }
  }

  async function moveThread() {
    if (anyBusy || !boardSlug || boardSlug === props.server.categorySlug) return
    const restoreSlug = props.server.categorySlug
    setBusyMove(true)
    setNotice(null)

    try {
      await apiFetch(`/api/moderation/threads/${encodeURIComponent(props.slug)}`, {
        method: 'PATCH',
        json: { categorySlug: boardSlug },
      })

      await props.onApplied()
    } catch {
      setNotice('Could not move: invalid board or network error.')
      setBoardSlug(restoreSlug)
    } finally {
      setBusyMove(false)
    }
  }

  return (
    <div className="space-y-6">
      {notice ? (
        <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] font-medium text-red-900">
          {notice}
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
        <div>
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Status</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={toggleCls(pinned)} disabled={anyBusy} onClick={() => void toggle('pinned', !pinned)}>
              {busyToggle === 'pinned' ? <IconLoader2 className="size-4 shrink-0 animate-spin" aria-hidden /> : <IconPin className="size-4 shrink-0" aria-hidden />}
              Pinned
            </button>

            <button type="button" className={toggleCls(locked)} disabled={anyBusy} onClick={() => void toggle('locked', !locked)}>
              {busyToggle === 'locked' ? <IconLoader2 className="size-4 shrink-0 animate-spin" aria-hidden /> : <IconLock className="size-4 shrink-0" aria-hidden />}
              Locked
            </button>

            <button type="button" className={toggleCls(hidden)} disabled={anyBusy} onClick={() => void toggle('hidden', !hidden)}>
              {busyToggle === 'hidden' ? (
                <IconLoader2 className="size-4 shrink-0 animate-spin" aria-hidden />
              ) : (
                <IconEyeOff className="size-4 shrink-0" aria-hidden />
              )}
              Hidden
            </button>
          </div>
          <p className={`${forumMutedSm} mt-3 max-w-[62ch] text-[12px] leading-snug text-zinc-600`}>
            Hidden keeps the topic off public indexes (direct link still works). Locked prevents neighbor replies unless a moderator replies.
          </p>
        </div>

        <div className="rounded-xl border border-zinc-200 bg-zinc-50/70 p-4">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Move to another board</p>
          <label htmlFor={idBoard} className="mb-2 block text-[13px] font-medium text-zinc-800">
            Category
          </label>

          <div className="flex flex-col gap-3">
            <div className="relative">
              <IconFolder className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-zinc-400" aria-hidden />
              <select
                id={idBoard}
                className={cn(
                  'w-full appearance-none rounded-lg border border-zinc-200 bg-white py-2.5 pr-10 pl-9 text-[14px] font-medium text-zinc-950 shadow-sm',
                  'focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400/35',
                  'disabled:bg-zinc-100 disabled:text-zinc-500',
                )}
                value={boardSlug}
                disabled={anyBusy || boards.length === 0}
                onChange={evt => setBoardSlug(evt.target.value)}
              >
                {boards.map(b => (
                  <option key={b.id} value={b.slug}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <Button
              type="button"
              variant="outline"
              className="rounded-lg border-indigo-400 font-semibold text-indigo-950 hover:bg-indigo-50"
              disabled={anyBusy || !boardSlug || boardSlug === props.server.categorySlug}
              onClick={() => void moveThread()}
            >
              {busyMove ? <IconLoader2 className="size-4 animate-spin" aria-hidden /> : null}
              Move thread
            </Button>
          </div>

          <p className={`${forumMutedSm} mt-3 text-[12px]`}>
            Saved under <strong className="text-zinc-800">{props.server.categoryName}</strong>.
          </p>
        </div>
      </div>
    </div>
  )
}
